import React, { forwardRef, useImperativeHandle, useState, useEffect, useRef } from 'react';
import { DashboardProject } from '../../../../types/project';
import { BuckleArrestorSetup, BuckleArrestorSetupRef } from './BuckleArrestorSetup';
import { PipeInPipeSetup, PipeInPipeSetupRef } from './PipeInPipeSetup';
import { BulkHeadSetup, BulkHeadSetupRef } from './BulkHeadSetup';
import { SpoolsSetup, SpoolsSetupRef } from './SpoolsSetup';
import type { SetupState, SetupState1, FabricationSetupData } from '../../../../types/setup';

interface FabricationSetupProps {
  project: DashboardProject;
  manufacturingList?: any[];
  setManufacturingList: React.Dispatch<React.SetStateAction<any[]>>;
  unit: string;
  setUnit?: (unit: string) => void;
  showValidation?: boolean;
  setupData?: any;
  buckleArrestorState?: SetupState1;
  setBuckleArrestorState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  pipeInPipeState?: SetupState1;
  setPipeInPipeState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  bulkheadState?: SetupState1;
  setBulkheadState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  spoolsState?: SetupState1;
  setSpoolsState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  canEdit?: boolean;
  onSaveBuckleArrestorPipe?: (pipeIndex: number) => Promise<boolean>;
  onSavePipeInPipePipe?: (pipeIndex: number) => Promise<boolean>;
  onSaveBulkheadPipe?: (pipeIndex: number) => Promise<boolean>;
  onSaveSpoolsPipe?: (pipeIndex: number) => Promise<boolean>;
}

export interface FabricationSetupRef {
  getSetupData: () => FabricationSetupData[];
}

export const FabricationSetup = forwardRef<FabricationSetupRef, FabricationSetupProps>(({ project, manufacturingList, 
  setManufacturingList, unit, setUnit, showValidation = false, setupData: propSetupData, buckleArrestorState, setBuckleArrestorState, pipeInPipeState, setPipeInPipeState, bulkheadState, setBulkheadState, spoolsState, setSpoolsState, setHasUnsavedChanges, canEdit = true, onSaveBuckleArrestorPipe, onSavePipeInPipePipe, onSaveBulkheadPipe, onSaveSpoolsPipe }, ref) => {
  const [setupData, setSetupData] = useState<FabricationSetupData[]>([]);

  // Refs to access child component data
  const buckleArrestorRef = useRef<BuckleArrestorSetupRef>(null);
  const pipeInPipeRef = useRef<PipeInPipeSetupRef>(null);
  const bulkHeadRef = useRef<BulkHeadSetupRef>(null);
  const spoolsRef = useRef<SpoolsSetupRef>(null);

  // Update setup data when prop changes
  useEffect(() => {
    
    if (propSetupData && Object.keys(propSetupData).length > 0) {
      // Log all values to see the structure
      
      // Filter fabrication data from the setupData
      // The data structure has keys as project type IDs and values as setup data objects
      const fabricationData: FabricationSetupData[] = [];
      
      Object.entries(propSetupData).forEach(([key, data]: [string, any]) => {
        if (data && data.projectType === 'fabrication' && data.pipeSizes && data.pipeSizes.length > 0) {
          fabricationData.push(data as FabricationSetupData);
        }
      });
      
      setSetupData(fabricationData);
    } else {
      // If no setupData, initialize with empty array
      setSetupData([]);
    }
  }, [propSetupData]);

  // Convert current state back to FabricationSetupData for the ref
  const getSetupData = (): FabricationSetupData[] => {
    const result: FabricationSetupData[] = [];

    // Collect data from each child component based on what's selected
    if (project.project.projectTypes.fabrication?.options?.buckleArrestor?.selected && buckleArrestorRef.current) {
      const buckleArrestorData = buckleArrestorRef.current.getSetupData();
      if (buckleArrestorData && buckleArrestorData.pipeSizes && buckleArrestorData.pipeSizes.length > 0) {
        result.push({
          projectId: parseInt(project.project.projectId),
          projectTypeId: project.project.projectTypes.fabrication?.project_type_id || 0,
          projectType: 'fabrication',
          projectSubType: 'buckleArrestor',
          numberOfPipeSizes: buckleArrestorData.numberOfPipeSizes || 0,
          pipeSizes: buckleArrestorData.pipeSizes || []
        });
      }
    }

    if (project.project.projectTypes.fabrication?.options?.pipeInPipe?.selected && pipeInPipeRef.current) {
      const pipeInPipeData = pipeInPipeRef.current.getSetupData();
      if (pipeInPipeData && pipeInPipeData.pipeSizes && pipeInPipeData.pipeSizes.length > 0) {
        result.push({
          projectId:parseInt(project.project.projectId),
          projectTypeId:  project.project.projectTypes.fabrication?.project_type_id || 0,
          projectType: 'fabrication',
          projectSubType: 'pipeInPipe',
          numberOfPipeSizes: pipeInPipeData.numberOfPipeSizes || 0,
          pipeSizes: pipeInPipeData.pipeSizes || []
        });
      }
    }

    if (project.project.projectTypes.fabrication?.options?.bulkhead?.selected && bulkHeadRef.current) {
      const bulkHeadData = bulkHeadRef.current.getSetupData();
      if (bulkHeadData && bulkHeadData.pipeSizes && bulkHeadData.pipeSizes.length > 0) {
        result.push({
          projectId: parseInt(project.project.projectId),
          projectTypeId: project.project.projectTypes.fabrication?.project_type_id || 0,
          projectType: 'fabrication',
          projectSubType: 'bulkhead',
          numberOfPipeSizes: bulkHeadData.numberOfPipeSizes || 0,
          pipeSizes: bulkHeadData.pipeSizes || []
        });
      }
    }

    if (project.project.projectTypes.fabrication?.options?.spools?.selected && spoolsRef.current) {
      const spoolsData = spoolsRef.current.getSetupData();
      if (spoolsData && spoolsData.pipeSizes && spoolsData.pipeSizes.length > 0) {
        result.push({
          projectId: parseInt(project.project.projectId),
          projectTypeId: project.project.projectTypes.fabrication?.project_type_id || 0,
          projectType: 'fabrication',
          projectSubType: 'spools',
          numberOfPipeSizes: spoolsData.numberOfPipeSizes || 0,
          pipeSizes: spoolsData.pipeSizes || []
        });
      }
    }

    return result;
  };

  // Expose setup data through ref
  useImperativeHandle(ref, () => ({
    getSetupData
  }));

  // if ( !project.project.projectTypes.fabrication?.options?.buckleArrestor?.selected && !project.project.projectTypes.fabrication?.options?.bulkhead?.selected && !project.project.projectTypes.fabrication?.options?.pipeInPipe?.selected) {
  //   return null;
  // }

  return (
    <div className="space-y-12">
      {project.project.projectTypes.fabrication?.options?.buckleArrestor?.selected && (
        <BuckleArrestorSetup 
          ref={buckleArrestorRef}
          project={project} 
          manufacturingList={manufacturingList} 
          setManufacturingList={setManufacturingList} 
          unit={unit} 
          setUnit={setUnit}
          showValidation={showValidation}
          setupData={setupData.find(data => data.projectTypeId === project.project.projectTypes.fabrication?.options?.buckleArrestor?.project_type_id)}
          state={buckleArrestorState}
          setState={setBuckleArrestorState}
          setHasUnsavedChanges={setHasUnsavedChanges}
          canEdit={canEdit}
          onSavePipe={onSaveBuckleArrestorPipe}
        />
      )}
      {project.project.projectTypes.fabrication?.options?.pipeInPipe?.selected && (
        <PipeInPipeSetup 
          ref={pipeInPipeRef}
          project={project} 
          manufacturingList={manufacturingList} 
          setManufacturingList={setManufacturingList} 
          unit={unit} 
          setUnit={setUnit}
          showValidation={showValidation}
          setupData={setupData.find(data => data.projectTypeId === project.project.projectTypes.fabrication?.options?.pipeInPipe?.project_type_id)}
          state={pipeInPipeState}
          setState={setPipeInPipeState}
          setHasUnsavedChanges={setHasUnsavedChanges}
          canEdit={canEdit}
          onSavePipe={onSavePipeInPipePipe}
          />
      )}
      {project.project.projectTypes.fabrication?.options?.bulkhead?.selected && (
        <BulkHeadSetup 
          ref={bulkHeadRef}
          project={project} 
          manufacturingList={manufacturingList} 
          setManufacturingList={setManufacturingList} 
          unit={unit} 
          setUnit={setUnit}
          showValidation={showValidation}
          setupData={setupData.find(data => data.projectTypeId === project.project.projectTypes.fabrication?.options?.bulkhead?.project_type_id)}
          state={bulkheadState}
          setState={setBulkheadState}
            setHasUnsavedChanges={setHasUnsavedChanges}
          canEdit={canEdit}
          onSavePipe={onSaveBulkheadPipe}
        />
      )}
      {project.project.projectTypes.fabrication?.options?.spools?.selected && (
        <SpoolsSetup
          ref={spoolsRef}
          project={project}
          manufacturingList={manufacturingList}
          setManufacturingList={setManufacturingList}
          unit={unit}
          setUnit={setUnit}
          showValidation={showValidation}
          setupData={setupData.find(data => data.projectTypeId === project.project.projectTypes.fabrication?.options?.spools?.project_type_id)}
          state={spoolsState}
          setState={setSpoolsState}
          setHasUnsavedChanges={setHasUnsavedChanges}
          canEdit={canEdit}
          onSavePipe={onSaveSpoolsPipe}
        />
      )}
    </div>
  );
}); 